import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { API } from "@/data/site";

export const KineticLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const StockBadge = ({ available }) => {
  const state =
    available > 5 ? { label: "In stock", cls: "text-[#0e8c8c] border-[#0e8c8c]/30 bg-[#0e8c8c]/5", dot: "bg-[#0e8c8c]" }
    : available > 0 ? { label: "Low stock", cls: "text-[#b4690e] border-[#b4690e]/30 bg-[#b4690e]/5", dot: "bg-[#b4690e]" }
    : { label: "Out of stock", cls: "text-[#b3261e] border-[#b3261e]/30 bg-[#b3261e]/5", dot: "bg-[#b3261e]" };
  return (
    <span
      className={`font-lab inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] uppercase tracking-[0.14em] ${state.cls}`}
      data-testid="stock-badge"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${state.dot}`} /> {state.label}
    </span>
  );
};

export const ProductCard = ({ product, index = 0 }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link
      to={`/products/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group block border border-[#e4e9ec] bg-white transition-colors duration-300 hover:border-[#0a0a0a]"
    >
      <div className="flex items-center justify-between border-b border-[#e4e9ec] px-4 py-2.5">
        <span className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#56636b]">{product.sku}</span>
        <span className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#0e8c8c]">{product.category}</span>
      </div>
      <div className="relative aspect-[3/4] overflow-hidden bg-[#f4f3ef]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="font-lab absolute bottom-3 left-3 bg-[#0a0a0a]/80 px-2 py-1 text-[9px] uppercase tracking-[0.14em] text-white backdrop-blur-sm">
          {product.potency}
        </span>
      </div>
      <div className="px-4 py-4">
        <h3 className="font-display text-lg leading-snug">{product.name}</h3>
        <p className="mt-0.5 text-xs text-[#56636b]">{product.tagline}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-medium" data-testid={`product-price-${product.slug}`}>
            ${(product.price_cents / 100).toFixed(2)}{" "}
            <span className="font-lab text-[10px] text-[#56636b]">CAD · suggested retail</span>
          </span>
          <span className="font-lab text-[10px] uppercase tracking-[0.14em] text-[#0a0a0a] underline-offset-4 group-hover:underline">
            View
          </span>
        </div>
      </div>
    </Link>
  </Reveal>
);

export const PartnerForm = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", region: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      toast.success("Thank you — our team will reach out about your region.");
      setForm({ name: "", email: "", organization: "", region: "", message: "" });
    } catch {
      toast.error("Could not send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const input =
    "w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#0e8c8c]";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="partner-form">
      <input required className={input} placeholder="Full name" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-form-name" />
      <input required type="email" className={input} placeholder="Email" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-form-email" />
      <input className={input} placeholder="Organization" value={form.organization}
        onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="partner-form-organization" />
      <input className={input} placeholder="Region / territory" value={form.region}
        onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="partner-form-region" />
      <textarea required rows={4} className={`${input} md:col-span-2`} placeholder="Tell us about your market" value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="partner-form-message" />
      <button
        type="submit"
        disabled={sending}
        data-testid="partner-form-submit"
        className="font-lab inline-flex w-fit items-center gap-3 bg-white px-8 py-3.5 text-[11px] uppercase tracking-[0.18em] text-[#070b0c] transition-colors hover:bg-[#0e8c8c] hover:text-white disabled:opacity-50 md:col-span-2"
      >
        {sending ? "Sending…" : "Request partnership"}
      </button>
    </form>
  );
};
